import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AttachIcon,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Control,
  Input,
  Label,
  Select,
  SelectOption,
  TextArea,
  useDocumentTitle,
} from '@forge/common';
import { setRequirements, setSelectedId, useIntakeStore } from './intakeStore';
import { formatBytes, newId } from './intakeUtils';
import type {
  Attachment,
  Requirement,
  RequirementPriority,
} from '@/data/mock-requirements';

const IntakeNewPage = () => {
  useDocumentTitle('New request · Intake · OneRing');
  const navigate = useNavigate();
  const { requirements } = useIntakeStore();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [priority, setPriority] = useState<RequirementPriority>('medium');
  const [files, setFiles] = useState<{ name: string; size: number }[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const canSubmit = Boolean(title.trim() && body.trim() && clientName.trim());

  const handleCreate = (): void => {
    const now = new Date().toISOString();
    const seq = 1040 + requirements.length;
    const id = newId();
    const trimmedTitle = title.trim();
    const trimmedClient = clientName.trim();
    const company = clientCompany.trim() || '—';

    const attachments: Attachment[] = files.map((f) => ({
      id: newId(),
      name: f.name,
      size: f.size,
      uploadedBy: trimmedClient,
      uploadedByRole: 'client',
      uploadedAt: now,
    }));

    const created: Requirement = {
      id,
      reference: `REQ-${seq}`,
      title: trimmedTitle,
      body,
      clientName: trimmedClient,
      clientCompany: company,
      status: 'draft',
      currentVersion: 1,
      priority,
      createdAt: now,
      updatedAt: now,
      versions: [
        {
          version: 1,
          statusAtChange: 'draft',
          title: trimmedTitle,
          body,
          changeSummary: 'Initial draft',
          changedBy: trimmedClient,
          changedByRole: 'client',
          changedAt: now,
        },
      ],
      events: [
        {
          id: newId(),
          type: 'created',
          actor: trimmedClient,
          actorRole: 'client',
          createdAt: now,
        },
      ],
      attachments,
      jiraIssues: [],
    };

    setRequirements((prev) => [created, ...prev]);
    setSelectedId(id);
    navigate(`/intake/${id}`);
  };

  return (
    <div className="space-y-4 p-4">
      <Breadcrumbs
        crumbs={[
          { id: 'intake', label: 'Intake', href: '/intake' },
          { id: 'new', label: 'New request', noAction: true },
        ]}
        onChange={({ id }) => {
          if (id === 'intake') navigate('/intake');
        }}
      />

      <div className="min-w-0">
        <h2 className="text-xl font-bold">New request</h2>
        <p className="mt-1 max-w-2xl text-sm text-color-tertiary">
          Capture the request in as much detail as possible. It starts as a draft —
          you can keep editing until you submit it for account manager review.
        </p>
      </div>

      <Card className="max-w-3xl">
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Control hideSpacing="bottom">
              <Label htmlFor="new-client">Client name</Label>
              <Input
                id="new-client"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            </Control>
            <Control hideSpacing="bottom">
              <Label htmlFor="new-company">Company</Label>
              <Input
                id="new-company"
                value={clientCompany}
                onChange={(e) => setClientCompany(e.target.value)}
              />
            </Control>
          </div>
          <Control hideSpacing="bottom">
            <Label htmlFor="new-title">Title</Label>
            <Input
              id="new-title"
              placeholder="Short summary of the request"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Control>
          <Control hideSpacing="bottom">
            <Label htmlFor="new-priority">Priority</Label>
            <Select
              id="new-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as RequirementPriority)}
            >
              <SelectOption value="low" label="Low">
                Low
              </SelectOption>
              <SelectOption value="medium" label="Medium">
                Medium
              </SelectOption>
              <SelectOption value="high" label="High">
                High
              </SelectOption>
              <SelectOption value="urgent" label="Urgent">
                Urgent
              </SelectOption>
            </Select>
          </Control>
          <Control hideSpacing="bottom">
            <Label htmlFor="new-body">Request details</Label>
            <TextArea
              id="new-body"
              rows={12}
              placeholder="Describe what you need, including sources, targets, schedules, and constraints…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </Control>

          <div>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []).map((f) => ({
                  name: f.name,
                  size: f.size,
                }));
                setFiles((prev) => [...prev, ...picked]);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              icon={<AttachIcon />}
              onClick={() => inputRef.current?.click()}
            >
              Attach files
            </Button>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, idx) => (
                  <li
                    key={`${f.name}-${idx}`}
                    className="flex items-center justify-between rounded-md bg-gray-100 px-2 py-1 text-xs dark:bg-gray-800"
                  >
                    <span className="truncate">{f.name}</span>
                    <span className="text-color-tertiary">{formatBytes(f.size)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
            <Button variant="secondary" onClick={() => navigate('/intake')}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!canSubmit} onClick={handleCreate}>
              Create draft
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntakeNewPage;
